<script setup>
// "We accept" — the payment methods a guest can actually use (2026-09-07):
// Visa, Mastercard, MIR and SBP through the secure payment link, cash or
// bank transfer on the day, and the Thai favourites PromptPay and TrueMoney.
// Marks are inline SVG in the systems' own
// colours so the row renders identically everywhere; swap a mark's inner
// SVG for the official asset if a brand supplies one. `compact` = the
// footer row (marks only, smaller).
import { useI18n } from 'vue-i18n'

// `inline` (2026-09-30, operator: "add everywhere") = the request cards, the
// plan form and the pay page: the "We accept" label and the marks on one
// line under the form, so a guest sees how to pay before asking.
defineProps({
  compact: { type: Boolean, default: false },
  inline:  { type: Boolean, default: false }
})

const { t } = useI18n()
const METHODS = ['visa', 'mastercard', 'mir', 'sbp', 'promptpay', 'truemoney', 'cash', 'bank_transfer']
</script>

<template>
  <div :class="['pm', (compact || inline) && 'pm--compact', inline && 'pm--inline']">
    <span v-if="inline" class="pm__inline-label">{{ t('public.payments.title') }}</span>
    <template v-if="!compact && !inline">
      <p class="pm__eyebrow"><i /><span>{{ t('public.payments.title') }}</span><i /></p>
      <p class="pm__lede">{{ t('public.payments.lede') }}</p>
    </template>
    <ul class="pm__row" :aria-label="t('public.payments.title')">
      <li v-for="m in METHODS" :key="m" class="pm__item">
        <span class="pm__mark" :title="t(`public.payments.methods.${m}`)">
          <svg v-if="m === 'visa'" viewBox="0 0 64 40" aria-hidden="true">
            <text x="32" y="26.5" text-anchor="middle" font-family="'Arial Black', Arial, Helvetica, sans-serif" font-weight="900" font-style="italic" font-size="17" letter-spacing="-0.5" fill="#1A1F71">VISA</text>
          </svg>
          <svg v-else-if="m === 'mastercard'" viewBox="0 0 64 40" aria-hidden="true">
            <circle cx="25.5" cy="20" r="11" fill="#EB001B" />
            <circle cx="38.5" cy="20" r="11" fill="#F79E1B" />
            <path d="M32 11.3a11 11 0 0 1 0 17.4 11 11 0 0 1 0-17.4z" fill="#FF5F00" />
          </svg>
          <svg v-else-if="m === 'mir'" viewBox="0 0 64 40" aria-hidden="true">
            <text x="32" y="26" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="16" letter-spacing="0.5" fill="#0F754E">МИР</text>
          </svg>
          <svg v-else-if="m === 'sbp'" viewBox="0 0 64 40" aria-hidden="true">
            <defs><linearGradient id="pm-sbp" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#5B57A2" /><stop offset="0.45" stop-color="#963A8E" /><stop offset="1" stop-color="#FAB718" /></linearGradient></defs>
            <rect x="4" y="6" width="56" height="28" rx="6" fill="url(#pm-sbp)" />
            <text x="32" y="25.5" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="14" letter-spacing="1" fill="#fff">СБП</text>
          </svg>
          <svg v-else-if="m === 'promptpay'" viewBox="0 0 64 40" aria-hidden="true">
            <g fill="#1E4E9A"><rect x="9" y="11" width="7" height="7" rx="1.2" /><rect x="9" y="22" width="7" height="7" rx="1.2" /><rect x="20" y="11" width="7" height="7" rx="1.2" /><rect x="20" y="22" width="3" height="3" rx="0.6" /><rect x="24" y="26" width="3" height="3" rx="0.6" /></g>
            <text x="44" y="19" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="8.5" fill="#1E4E9A">Prompt</text>
            <text x="44" y="28" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="8.5" fill="#1E4E9A">Pay</text>
          </svg>
          <svg v-else-if="m === 'truemoney'" viewBox="0 0 64 40" aria-hidden="true">
            <rect x="8" y="9" width="22" height="22" rx="5" fill="#F58220" />
            <text x="19" y="25.5" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="900" font-size="14" fill="#fff">T</text>
            <text x="46" y="19" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="8" fill="#F58220">True</text>
            <text x="46" y="28" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="8" fill="#F58220">Money</text>
          </svg>
          <svg v-else-if="m === 'cash'" viewBox="0 0 64 40" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="12" y="11" width="40" height="19" rx="3" />
            <circle cx="32" cy="20.5" r="5" />
            <path d="M17 16h2M45 25h2" />
          </svg>
          <svg v-else viewBox="0 0 64 40" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M16 17l16-8 16 8H16z" />
            <path d="M19 17v11M26 17v11M32 17v11M38 17v11M45 17v11" />
            <path d="M15 29h34M14 32h36" />
          </svg>
        </span>
        <span v-if="!compact && !inline" class="pm__label">{{ t(`public.payments.methods.${m}`) }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.pm { display: grid; gap: 0.9rem; justify-items: center; text-align: center; }
.pm__eyebrow { display: inline-flex; align-items: center; gap: 0.75rem; margin: 0; font-size: 0.68rem; letter-spacing: var(--eyebrow-tracking, 0.18em); text-transform: uppercase; font-weight: 600; color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); }
.pm__eyebrow i { width: 2rem; height: 1px; background: color-mix(in oklch, var(--gold, var(--warn)) 55%, var(--line)); }
.pm__lede { margin: 0; max-width: 46ch; color: var(--inkSoft); line-height: 1.5; font-size: 0.95rem; }
.pm__row { list-style: none; margin: 0.35rem 0 0; padding: 0; display: flex; flex-wrap: wrap; justify-content: center; gap: 1rem 1.25rem; }
.pm__item { display: grid; gap: 0.4rem; justify-items: center; }
.pm__mark { display: grid; place-items: center; width: 4.25rem; height: 2.65rem; border-radius: 0.55rem; background: #fff; border: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 30%, var(--line)); color: #0B2537; box-shadow: 0 1px 2px color-mix(in oklch, var(--ink) 6%, transparent); }
.pm__mark svg { width: 100%; height: 100%; display: block; }
.pm__label { font-size: 0.72rem; color: var(--muted); letter-spacing: 0.01em; white-space: nowrap; }
/* footer row */
.pm--compact { justify-items: start; text-align: left; gap: 0; }
.pm--compact .pm__row { justify-content: flex-start; gap: 0.5rem; margin: 0; }
.pm--compact .pm__mark { width: 3.1rem; height: 1.95rem; border-radius: 0.4rem; box-shadow: none; }
/* under a request card / form: label + marks on one wrapping line */
.pm--inline { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem 0.8rem; margin-top: 1rem; padding-top: 0.9rem; border-top: 1px dashed var(--line); }
.pm__inline-label { font-size: 0.66rem; letter-spacing: var(--eyebrow-tracking, 0.14em); text-transform: uppercase; font-weight: 700; color: var(--muted); }
.pm--inline .pm__mark { width: 2.7rem; height: 1.7rem; }
</style>
